import React from 'react';
import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

// Wraps query matches in <mark> for highlighting
const highlightText = (text, query) => {
  if (!query || !text) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = String(text).split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i} className="bg-yellow-200 text-yellow-900 rounded px-0.5">{part}</mark>
      : part
  );
};

const SectionContent = ({ section, searchQuery = '', showAnnotations }) => {
  const [showOcrOverlays, setShowOcrOverlays] = useState(true);
  const [selectedOcrImage, setSelectedOcrImage] = useState(null);

  // Render section content based on type
  const renderContent = () => {
    // Normalize: anything that isn't explicitly 'list' or 'table' renders as text.
    // The AI sometimes returns types like 'paragraph', 'analysis', 'structured', etc.
    const type = section?.type;
    const isText = !type || type === 'text' || (type !== 'list' && type !== 'table');

    if (isText) {
      return (
        <div className="whitespace-pre-wrap text-gray-700">
          {highlightText(section?.content, searchQuery)}
        </div>
      );
    }

    if (section?.type === 'list') {
      const items = Array.isArray(section?.items) 
        ? section?.items 
        : (typeof section?.content === 'string' ? section?.content?.split('\n')?.filter(Boolean) 
            : []);

      const filtered = searchQuery
        ? items?.filter(item => item?.toLowerCase()?.includes(searchQuery?.toLowerCase()))
        : items;

      if (searchQuery && filtered?.length === 0) {
        return <p className="text-muted-foreground italic">No results found for "{searchQuery}"</p>;
      }

      return (
        <ul className="list-disc list-inside space-y-2 text-gray-700">
          {filtered?.map((item, idx) => {
            const cleanItem = item?.replace(/^[•\-*]\s*/, '');
            return <li key={idx}>{highlightText(cleanItem, searchQuery)}</li>;
          })}
        </ul>
      );
    }

    if (section?.type === 'table' && section?.data) {
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {section?.data?.headers?.map((header, idx) => (
                  <th key={idx} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {highlightText(header, searchQuery)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {section?.data?.rows?.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  {row?.map((cell, cellIdx) => (
                    <td key={cellIdx} className="px-4 py-3 text-sm text-gray-700">
                      {highlightText(cell, searchQuery)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return null;
  };

  // Render OCR results for Materials Reviewed section
  const renderOcrResults = () => {
    if (section?.number !== '4' || !section?.ocrResults?.length) return null;

    return (
      <div className="mt-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Extracted Handwritten Text (OCR)</h3>
          <button
            onClick={() => setShowOcrOverlays(!showOcrOverlays)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {showOcrOverlays ? (
              <>
                <EyeOff className="w-4 h-4" />
                Hide OCR Overlays
              </>
            ) : (
              <>
                <Eye className="w-4 h-4" />
                Show OCR Overlays
              </>
            )}
          </button>
        </div>

        {section?.ocrResults?.map((ocr, idx) => {
          const confidencePercent = (ocr?.confidence * 100)?.toFixed(0);
          const confidenceColor = ocr?.confidence >= 0.8 ? 'text-green-600 bg-green-50' : 
                                 ocr?.confidence >= 0.6 ? 'text-yellow-600 bg-yellow-50': 'text-red-600 bg-red-50';
          const qualityBadge = ocr?.quality === 'good' ? 'bg-green-100 text-green-800' :
                              ocr?.quality === 'fair'? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800';

          return (
            <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">{ocr?.file_name}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${confidenceColor}`}>
                      Confidence: {confidencePercent}%
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${qualityBadge}`}>
                      {ocr?.quality?.toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-500">Language: {ocr?.language?.toUpperCase()}</span>
                  </div>
                </div>
                {ocr?.image_url && (
                  <button
                    onClick={() => setSelectedOcrImage(selectedOcrImage === idx ? null : idx)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {selectedOcrImage === idx ? 'Hide Image' : 'View Image'}
                  </button>
                )}
              </div>

              {selectedOcrImage === idx && ocr?.image_url && (
                <div className="mb-3 relative">
                  <img 
                    src={ocr?.image_url} 
                    alt={`OCR source: ${ocr?.file_name}`}
                    className="w-full max-h-96 object-contain rounded border border-gray-300"
                  />
                  {showOcrOverlays && ocr?.regions?.length > 0 && (
                    <svg className="absolute inset-0 w-full h-full pointer-events-none">
                      {ocr?.regions?.map((region, rIdx) => (
                        <g key={rIdx}>
                          <rect
                            x={`${region?.bounding_box?.x}%`}
                            y={`${region?.bounding_box?.y}%`}
                            width={`${region?.bounding_box?.width}%`}
                            height={`${region?.bounding_box?.height}%`}
                            fill="rgba(59, 130, 246, 0.1)"
                            stroke="#3b82f6"
                            strokeWidth="2"
                          />
                          <text
                            x={`${region?.bounding_box?.x + 2}%`}
                            y={`${region?.bounding_box?.y + 4}%`}
                            fill="#1e40af"
                            fontSize="12"
                            fontWeight="bold"
                          >
                            {(region?.confidence * 100)?.toFixed(0)}%
                          </text>
                        </g>
                      ))}
                    </svg>
                  )}
                </div>
              )}

              <div className="bg-white border border-gray-200 rounded p-3">
                <p className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                  {ocr?.text || 'No text extracted'}
                </p>
              </div>

              {ocr?.regions?.length > 0 && (
                <details className="mt-3">
                  <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-900">
                    View {ocr?.regions?.length} text region{ocr?.regions?.length !== 1 ? 's' : ''} with confidence scores
                  </summary>
                  <div className="mt-2 space-y-2">
                    {ocr?.regions?.map((region, rIdx) => (
                      <div key={rIdx} className="text-xs bg-white border border-gray-200 rounded p-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-gray-700">Region {rIdx + 1}</span>
                          <span className={`px-1.5 py-0.5 rounded ${region?.confidence >= 0.8 ? 'bg-green-100 text-green-700' : region?.confidence >= 0.6 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                            {(region?.confidence * 100)?.toFixed(0)}%
                          </span>
                        </div>
                        <p className="text-gray-600 font-mono">{region?.text}</p>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div id={`section-${section?.id}`} className="mb-8 md:mb-12 scroll-mt-20">
      <div className="mb-4 md:mb-6">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
          {section?.number}. {section?.title}
        </h2>
        {section?.subtitle && (
          <p className="text-base md:text-lg text-muted-foreground">{section?.subtitle}</p>
        )}
      </div>
      <div className="bg-card rounded-lg p-4 md:p-6 lg:p-8 border border-border">
        <div className="prose prose-sm max-w-none">
          {renderContent()}
          {renderOcrResults()}
        </div>
      </div>
    </div>
  );
};

export default SectionContent;